import { useEffect, useState } from "react";
import Container from "@/components/Container";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { ExternalLink, Copy, Save, History, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const DEFAULT_DOMAINS = [
  "rightmove.co.uk","zoopla.co.uk","onthemarket.com","primelocation.com",
  "auctionhouse.co.uk","iamsold.co.uk","allsop.co.uk","savills.co.uk",
  "knightfrank.co.uk","ukpropertyauctions.co.uk","barnardmarcusauctions.co.uk",
  "sdlauctions.co.uk","cliveemson.co.uk","eigroup.co.uk","gov.uk","local.gov.uk",
];

const FRESHNESS = [
  { value: "qdr:d", label: "24h" },
  { value: "qdr:w", label: "Week" },
  { value: "qdr:m", label: "Month" },
  { value: "qdr:y", label: "Year" },
  { value: "", label: "Any time" },
];
const freshLabel = (v) => (FRESHNESS.find(f => f.value === v)?.label || "Any time");

// Parse Google's date strings ("2 days ago" / "Nov 12, 2025") into a timestamp for sorting.
const parseResultDate = (s) => {
  if (!s) return 0;
  const rel = s.match(/(\d+)\s+(minute|hour|day|week|month|year)s?\s+ago/i);
  if (rel) {
    const mult = { minute: 6e4, hour: 36e5, day: 864e5, week: 6048e5, month: 2592e6, year: 31536e6 }[rel[2].toLowerCase()];
    return Date.now() - Number(rel[1]) * mult;
  }
  const t = Date.parse(s);
  return isNaN(t) ? 0 : t;
};

const fmtPrice = (n) => "£" + Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 });

export default function SearchEngine() {
  const [groups, setGroups] = useState({});
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [excludedKeywords, setExcludedKeywords] = useState([]);
  const [domains, setDomains] = useState(["rightmove.co.uk","zoopla.co.uk","auctionhouse.co.uk"]);
  const [locations, setLocations] = useState("");
  const [queries, setQueries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [usage, setUsage] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [latestResults, setLatestResults] = useState([]);
  const [runningQuery, setRunningQuery] = useState("");
  const [tbs, setTbs] = useState("qdr:m");
  const [resultSort, setResultSort] = useState("score");

  const refreshMeta = () => {
    api.get("/search-engine/usage").then(r => setUsage(r.data)).catch(() => {});
    api.get("/search-engine/runs?limit=10").then(r => { setRuns(r.data?.rows || []); setRunsTotal(r.data?.total || 0); }).catch(() => {});
  };

  useEffect(() => {
    api.get("/keyword-groups").then(r => setGroups(r.data)).catch(() => {});
    refreshMeta();
  }, []);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  const generate = async () => {
    try {
      const { data } = await api.post("/search-engine/generate", {
        locations: locations.split(",").map(l => l.trim()).filter(Boolean),
        keywords: selectedKeywords,
        excluded_keywords: excludedKeywords,
        domains,
        tbs: tbs || null,
      });
      setQueries(data.queries || []);
      toast.success(`Generated ${data.count} queries`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAsSavedSearch = async () => {
    if (!searchName) { toast.error("Enter a name"); return; }
    setSaving(true);
    try {
      await api.post("/saved-searches", {
        name: searchName,
        country: "United Kingdom",
        city: locations.split(",")[0]?.trim() || "",
        keywords: selectedKeywords,
        excluded_keywords: excludedKeywords,
        source_domains: domains,
      });
      toast.success("Search saved");
      setSearchName("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const copy = (q) => { navigator.clipboard.writeText(q); toast.success("Copied"); };

  const runLive = async (query, tbsOverride) => {
    if (usage && usage.remaining <= 0) {
      toast.error(`Monthly cap reached (${usage.used}/${usage.limit}). Wait until next month.`);
      return;
    }
    const effectiveTbs = tbsOverride !== undefined ? tbsOverride : tbs;
    setRunningQuery(query);
    setLatestResults([]);
    try {
      toast.info(`Running via SerpAPI (${freshLabel(effectiveTbs)})…`);
      const { data } = await api.post("/search-engine/execute", { query, num: 20, tbs: effectiveTbs || null });
      toast.success(`Found ${data.total_results} · ${data.inserted} new · ${data.duplicates} duplicates`);
      refreshMeta();
      // Fetch the freshly-ingested results and show them inline
      try {
        const rr = await api.get("/search-engine/runs/" + data.run_id);
        setLatestResults(rr.data?.results || []);
      } catch { /* silent */ }
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error(e.response.data?.detail || "Monthly SerpAPI cap reached");
        refreshMeta();
      } else {
        toast.error(formatApiError(e.response?.data?.detail, "Live search failed"));
      }
    }
  };

  const replayRun = (q, runTbs) => { setQueries([{ query: q, google_url: `https://www.google.com/search?q=${encodeURIComponent(q)}` }]); runLive(q, runTbs ?? ""); };

  const viewRun = async (r) => {
    // Fetch and display the results from a previously-run query — no new SerpAPI credit spent.
    setRunningQuery(r.query);
    setLatestResults([]);
    try {
      const { data } = await api.get("/search-engine/runs/" + r.id);
      setLatestResults(data?.results || []);
      // scroll the freshly-populated block into view
      setTimeout(() => {
        document.querySelector('[data-testid="latest-run-results"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      if ((data?.results || []).length === 0) toast.info("No stored results for that run.");
    } catch {
      toast.error("Could not load run results.");
    }
  };

  const deleteRun = async (id) => {
    if (!confirm("Delete this run history?")) return;
    await api.delete(`/search-engine/runs/${id}`);
    refreshMeta();
  };

  const usedPct = usage ? Math.min(100, (usage.used / usage.limit) * 100) : 0;
  const usageColor = usedPct > 90 ? "bg-ruby" : usedPct > 70 ? "bg-bronze" : "bg-gold";

  return (
    <Container className="py-8 md:py-12">
      <p className="overline">Discovery</p>
      <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Search Engine</h1>
      <p className="text-ivory/50 mt-2 text-sm">Generate site-targeted Google queries · run live via SerpAPI · ingest results with images, links and snippets.</p>

      {/* Usage meter */}
      {usage && (
        <div className="luxury-card p-5 mt-8" data-testid="serpapi-usage">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ivory/50">SerpAPI usage · {usage.month}</p>
              <p className="mt-2 font-serif text-2xl text-ivory">
                {usage.used} <span className="text-ivory/40">/ {usage.limit}</span>
              </p>
            </div>
            <p className="text-xs text-ivory/60">
              <span className="text-gold">{usage.remaining}</span> searches remaining this month
            </p>
          </div>
          <div className="mt-3 h-1.5 w-full bg-charcoal-700 overflow-hidden">
            <div className={`h-full ${usageColor} transition-all`} style={{ width: `${usedPct}%` }} />
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="luxury-card p-6 space-y-5">
          <div>
            <p className="overline">Locations (comma separated)</p>
            <input
              value={locations} onChange={e => setLocations(e.target.value)}
              placeholder="Birmingham, Manchester, M14"
              data-testid="search-locations"
              className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none"
            />
          </div>
          <div>
            <p className="overline">Source domains</p>
            <div className="mt-2 max-h-52 overflow-y-auto space-y-1.5 pr-2">
              {DEFAULT_DOMAINS.map(d => (
                <label key={d} className="flex items-center gap-2 text-sm text-ivory/80">
                  <input type="checkbox" checked={domains.includes(d)} onChange={() => toggle(domains, setDomains, d)} className="accent-ruby" />
                  {d}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="overline">Listed within</p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="freshness-pills">
              {FRESHNESS.map(f => (
                <button
                  key={f.value || "any"}
                  onClick={() => setTbs(f.value)}
                  data-testid={`freshness-${f.value ? f.value.replace(":", "-") : "any"}`}
                  className={`px-2.5 py-1 text-[0.7rem] uppercase tracking-wider border transition-colors ${
                    tbs === f.value ? "border-gold text-gold" : "border-charcoal-600 text-ivory/60 hover:border-ivory/40"
                  }`}
                >{f.label}</button>
              ))}
            </div>
            <p className="mt-1.5 text-[0.65rem] text-ivory/40">Only return listings Google indexed in this window — kills stale results.</p>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={generate} className="btn-ruby justify-center" data-testid={ADMIN.generateQueries}>
              Generate Queries
            </button>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={searchName} onChange={e => setSearchName(e.target.value)}
                placeholder="Save as…" data-testid="search-name"
                className="flex-1 min-w-0 bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none"
              />
              <button onClick={saveAsSavedSearch} disabled={saving} className="btn-outline justify-center shrink-0" data-testid={ADMIN.saveSearch}>
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 luxury-card p-6">
          <p className="overline">Keyword groups</p>
          <div className="mt-4 space-y-5 max-h-[28rem] overflow-y-auto pr-2">
            {Object.entries(groups).map(([gName, kws]) => (
              <div key={gName}>
                <p className="text-xs uppercase tracking-[0.18em] text-gold mb-2">{gName}</p>
                <div className="flex flex-wrap gap-1.5">
                  {kws.map(k => {
                    const active = (gName === "exclusion" ? excludedKeywords : selectedKeywords).includes(k);
                    return (
                      <button
                        key={k}
                        onClick={() => gName === "exclusion"
                          ? toggle(excludedKeywords, setExcludedKeywords, k)
                          : toggle(selectedKeywords, setSelectedKeywords, k)}
                        className={`px-2 py-1 text-[0.7rem] uppercase tracking-wider border transition-colors ${
                          active
                            ? gName === "exclusion" ? "border-ruby-light text-ruby-light" : "border-gold text-gold"
                            : "border-charcoal-600 text-ivory/60 hover:border-ivory/40"
                        }`}
                      >{k}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Generated queries */}
      <div className="mt-10">
        <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
          <p className="overline">Generated queries ({queries.length})</p>
          <p className="text-xs text-ivory/40">Open in Google · run live · copy</p>
        </div>
        <div className="luxury-card divide-y divide-charcoal-600/60 max-h-[36rem] overflow-y-auto">
          {queries.length === 0 && <p className="p-8 text-center text-ivory/40 text-sm">Configure & generate to see queries.</p>}
          {queries.map((q, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-4 hover:bg-charcoal-800/60 transition-colors" data-testid={`query-row-${i}`}>
              <code className="text-xs text-ivory/80 font-mono break-all flex-1">{q.query}</code>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button onClick={() => runLive(q.query)} className="btn-ruby !py-1.5 !px-3 !text-[0.65rem]" data-testid={`query-run-${i}`}>
                  Run Live
                </button>
                <a href={q.google_url} target="_blank" rel="noopener noreferrer" className="btn-outline !py-1.5 !px-3 !text-[0.65rem]">
                  <ExternalLink size={12} /> Open
                </a>
                <button onClick={() => copy(q.query)} className="btn-outline !py-1.5 !px-3 !text-[0.65rem]">
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Latest run results — appear inline after a Run Live */}
      {latestResults.length > 0 && (() => {
        const sorted = [...latestResults].sort((a, b) => {
          if (resultSort === "newest") return parseResultDate(b.result_date) - parseResultDate(a.result_date);
          if (resultSort === "price-low") return (a.possible_price || Infinity) - (b.possible_price || Infinity);
          if (resultSort === "price-high") return (b.possible_price || 0) - (a.possible_price || 0);
          return (b.opportunity_score || 0) - (a.opportunity_score || 0);
        });
        return (
        <div className="mt-10 luxury-card p-5" data-testid="latest-run-results">
          <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
            <div className="min-w-0">
              <h2 className="text-xl md:text-2xl font-serif text-ivory">Latest run · {latestResults.length} results</h2>
              <p className="text-xs text-ivory/50 mt-1 font-mono break-all">{runningQuery}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <select
                value={resultSort}
                onChange={(e) => setResultSort(e.target.value)}
                data-testid="results-sort"
                className="bg-charcoal-800 border border-charcoal-600 px-2 py-1.5 text-[0.7rem] uppercase tracking-wider text-ivory"
              >
                <option value="score">Best score</option>
                <option value="newest">Newest</option>
                <option value="price-low">Price: low → high</option>
                <option value="price-high">Price: high → low</option>
              </select>
              <button
                onClick={async () => {
                  if (!runningQuery) return;
                  const name = prompt("Name this search (so you can re-run it without spending another credit):",
                    locations.split(",")[0]?.trim() || "Saved search");
                  if (!name) return;
                  try {
                    await api.post("/saved-searches", {
                      name,
                      country: "United Kingdom",
                      city: locations.split(",")[0]?.trim() || "",
                      keywords: selectedKeywords,
                      excluded_keywords: excludedKeywords,
                      source_domains: domains,
                      generated_queries: [{ query: runningQuery }],
                    });
                    toast.success(`Saved "${name}" — reopen from Saved Searches to re-run without a new credit.`);
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail, "Save failed"));
                  }
                }}
                className="btn-ruby !py-1.5 !px-3 !text-[0.65rem]"
                data-testid="save-this-search"
              >
                <Save size={12} /> Save this search
              </button>
              <Link to="/admin/new-finds" className="btn-outline !py-1.5 !px-3 !text-[0.65rem]">Open in New Finds →</Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {sorted.map(r => (
              <a key={r.id} href={r.source_url} target="_blank" rel="noopener noreferrer"
                 className="border border-charcoal-600 hover:border-gold overflow-hidden flex flex-col transition-colors"
                 data-testid={`latest-${r.id}`}>
                <div className="w-full aspect-[16/10] bg-charcoal-800 overflow-hidden relative">
                  {r.image_url ? (
                    <img src={r.image_url} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.opacity = 0.2} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-ivory/30 text-[0.6rem] uppercase tracking-[0.18em]">{r.source_domain}</div>
                  )}
                  <div className="absolute top-2 left-2 flex gap-1.5">
                    <span className="bg-charcoal-950/85 backdrop-blur-sm border border-gold/50 text-gold text-[0.6rem] font-mono px-1.5 py-0.5" data-testid={`opp-badge-${r.id}`}>
                      OPP {Math.round(r.opportunity_score || 0)}
                    </span>
                    <span className="bg-charcoal-950/85 backdrop-blur-sm border border-ruby/50 text-ruby-light text-[0.6rem] font-mono px-1.5 py-0.5">
                      RISK {Math.round(r.risk_score || 0)}
                    </span>
                  </div>
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[0.6rem] uppercase tracking-[0.15em] text-gold truncate">{r.source_domain}</p>
                    {r.result_date && <p className="text-[0.6rem] text-ivory/40 shrink-0">{r.result_date}</p>}
                  </div>
                  <p className="mt-1 text-ivory text-sm line-clamp-2">{r.title}</p>
                  {r.snippet && <p className="mt-1 text-[0.7rem] text-ivory/50 line-clamp-2">{r.snippet}</p>}
                  <div className="mt-auto pt-2 flex flex-wrap items-center gap-1.5">
                    {r.possible_price && (
                      <span className="text-[0.7rem] font-mono text-ivory border border-charcoal-600 px-1.5 py-0.5" data-testid={`price-badge-${r.id}`}>
                        {fmtPrice(r.possible_price)}
                      </span>
                    )}
                    {(r.matched_keywords || []).slice(0, 3).map(k => (
                      <span key={k} className="text-[0.6rem] uppercase tracking-wider text-ivory/50 border border-charcoal-600/70 px-1.5 py-0.5">{k}</span>
                    ))}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
        );
      })()}

      {/* Saved runs history */}
      <div className="mt-10" data-testid="search-runs-section">
        <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
          <h2 className="text-sm font-serif text-ivory/90 inline-flex items-center gap-2">
            <History size={14} className="text-gold" /> Previous runs <span className="text-ivory/40">({runsTotal})</span>
          </h2>
          <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ivory/40">Click a row to view its results</p>
        </div>
        <div className="luxury-card divide-y divide-charcoal-600/60">
          {runs.length === 0 && <p className="p-6 text-center text-ivory/40 text-sm">No runs yet — kick off your first live search above.</p>}
          {runs.map(r => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => viewRun(r)}
              onKeyDown={(e) => { if (e.key === "Enter") viewRun(r); }}
              className="w-full text-left flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 hover:bg-charcoal-800/50 transition-colors cursor-pointer"
              data-testid={`run-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <code className="text-xs text-ivory/80 font-mono break-all">{r.query}</code>
                <p className="mt-1 text-[0.65rem] uppercase tracking-[0.18em] text-ivory/40">
                  {new Date(r.created_at).toLocaleString()} · {r.actor} · <span className="text-gold/70">{freshLabel(r.tbs || "")}</span> · click to view {r.inserted || 0} result{r.inserted === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                <div className="text-[0.65rem] uppercase tracking-[0.15em] text-ivory/50 flex gap-3">
                  <span><span className="text-ivory">{r.total_results}</span> found</span>
                  <span><span className="text-gold">{r.inserted}</span> new</span>
                  <span><span className="text-ivory/60">{r.duplicates}</span> dupes</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); replayRun(r.query, r.tbs || ""); }}
                  className="btn-outline !py-1.5 !px-3 !text-[0.65rem]"
                  data-testid={`run-replay-${r.id}`}
                >
                  Re-run
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); deleteRun(r.id); }}
                  className="btn-outline !py-1.5 !px-2 !text-[0.65rem]"
                  data-testid={`run-delete-${r.id}`}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
}
